import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
	ChevronDown,
	ChevronRight,
	ChevronUp,
	ExternalLink,
	MoreVertical,
	Pencil,
	Plus,
	Trash2
} from "lucide-react";
import { cn } from "src/lib/utils";

interface NavigationTreeItemProps {
	node: NavigationTreeNode;
	depth: number;
	selectedId: string | null;
	onSelect: (item: NavigationItem) => void;
	onAddChild: (parentId: string) => void;
	onMoveUp: (itemId: string) => void;
	onMoveDown: (itemId: string) => void;
	onDelete: (itemId: string) => void;
	canMoveUp: (itemId: string) => boolean;
	canMoveDown: (itemId: string) => boolean;
}

const NavigationTreeItem = ({
	node,
	depth,
	selectedId,
	onSelect,
	onAddChild,
	onMoveUp,
	onMoveDown,
	onDelete,
	canMoveUp,
	canMoveDown
}: NavigationTreeItemProps) => {
	const [expanded, setExpanded] = useState(true);
	const [menuOpen, setMenuOpen] = useState(false);
	const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
	const buttonRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const hasChildren = node.children.length > 0;
	const isSelected = selectedId === node.item.id;

	const updatePosition = useCallback(() => {
		if (!buttonRef.current) return;
		const rect = buttonRef.current.getBoundingClientRect();
		setMenuPos({ top: rect.bottom + 4, left: rect.right - 176 });
	}, []);

	useEffect(() => {
		if (!menuOpen) return;
		const handleClickOutside = (e: MouseEvent) => {
			if (
				menuRef.current &&
				!menuRef.current.contains(e.target as Node) &&
				buttonRef.current &&
				!buttonRef.current.contains(e.target as Node)
			) {
				setMenuOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [menuOpen, updatePosition]);

	const handleMenuToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		updatePosition();
		setMenuOpen((prev) => !prev);
	};

	const handleAction = (e: React.MouseEvent, action: () => void) => {
		e.stopPropagation();
		setMenuOpen(false);
		action();
	};

	return (
		<div>
			<div
				className={cn(
					"flex items-center px-4 py-3 border-b border-border cursor-pointer transition-colors",
					isSelected ? "bg-white" : "hover:bg-white/60"
				)}
				onClick={() => onSelect(node.item)}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onSelect(node.item);
					}
				}}
			>
				{/* Name column with indentation and icon */}
				<div
					className="flex items-center gap-2 flex-1 min-w-0"
					style={{ paddingLeft: `${depth * 24}px` }}
				>
					{hasChildren ? (
						<button
							type="button"
							aria-label={expanded ? "Collapse" : "Expand"}
							onClick={(e) => {
								e.stopPropagation();
								setExpanded(!expanded);
							}}
							className="p-0.5 shrink-0"
						>
							{expanded ? (
								<ChevronDown className="h-4 w-4 text-muted-foreground" />
							) : (
								<ChevronRight className="h-4 w-4 text-muted-foreground" />
							)}
						</button>
					) : node.item.web ? (
						<ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
					) : (
						<span className="w-5 shrink-0" />
					)}
					<span className="text-sm font-medium truncate">{node.item.name}</span>
				</div>

				{/* Layout column */}
				<div className="w-16 text-center shrink-0">
					{node.item.layout && (
						<span className="text-[11px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 font-medium uppercase">
							{node.item.layout}
						</span>
					)}
				</div>

				{/* Status column */}
				<div className="w-16 text-center shrink-0">
					<span
						className={cn(
							"text-[11px] px-1.5 py-0.5 rounded font-medium",
							node.item.published
								? "bg-green-50 text-green-700"
								: "bg-zinc-100 text-zinc-500"
						)}
					>
						{node.item.published ? "Live" : "Draft"}
					</span>
				</div>

				{/* Actions column */}
				<div className="w-10 flex justify-center shrink-0">
					<button
						ref={buttonRef}
						type="button"
						onClick={handleMenuToggle}
						aria-label="Item actions"
						className="p-2 rounded-lg hover:bg-muted focus:bg-muted flex items-center justify-center"
					>
						<MoreVertical size={16} />
					</button>

					{menuOpen &&
						createPortal(
							<div
								ref={menuRef}
								aria-label="item-menu-options"
								className="fixed w-44 bg-white border border-border rounded-md shadow-lg z-50 py-1"
								style={{ top: menuPos.top, left: menuPos.left }}
							>
								<button
									onClick={(e) => handleAction(e, () => onSelect(node.item))}
									className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left"
								>
									<Pencil size={16} />
									<span className="ml-3">Edit</span>
								</button>

								<button
									onClick={(e) => {
										e.stopPropagation();
										setMenuOpen(false);
										onAddChild(node.item.id);
										if (!expanded) setExpanded(true);
									}}
									className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left"
								>
									<Plus size={16} />
									<span className="ml-3">Add Child</span>
								</button>

								{canMoveUp(node.item.id) && (
									<button
										onClick={(e) =>
											handleAction(e, () => onMoveUp(node.item.id))
										}
										className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left"
									>
										<ChevronUp size={16} />
										<span className="ml-3">Move Up</span>
									</button>
								)}

								{canMoveDown(node.item.id) && (
									<button
										onClick={(e) =>
											handleAction(e, () => onMoveDown(node.item.id))
										}
										className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left"
									>
										<ChevronDown size={16} />
										<span className="ml-3">Move Down</span>
									</button>
								)}

								<button
									onClick={(e) => handleAction(e, () => onDelete(node.item.id))}
									className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left text-destructive"
								>
									<Trash2 size={16} />
									<span className="ml-3">Delete</span>
								</button>
							</div>,
							document.body
						)}
				</div>
			</div>

			{/* Children */}
			{hasChildren && expanded && (
				<div>
					{node.children.map((child) => (
						<NavigationTreeItem
							key={child.item.id}
							node={child}
							depth={depth + 1}
							selectedId={selectedId}
							onSelect={onSelect}
							onAddChild={onAddChild}
							onMoveUp={onMoveUp}
							onMoveDown={onMoveDown}
							onDelete={onDelete}
							canMoveUp={canMoveUp}
							canMoveDown={canMoveDown}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export default NavigationTreeItem;
